import React, { useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { createPartnerLink } from "../../../api/partnersApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";

const PartnerLinkFormModal = ({ isOpen, onClose, onSuccess }) => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId } = useBusinessUnit();

  const [partnerCode, setPartnerCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!partnerCode.trim()) {
      toast({ title: t("partners.partnerLinks.codeRequired", "Partner code is required"), status: "warning", duration: 3000 });
      return;
    }
    setLoading(true);
    try {
      await createPartnerLink(
        { partnerCode: partnerCode.trim() },
        { buId: selectedBusinessUnitId || undefined }
      );
      toast({ title: t("partners.partnerLinks.created", "Partner link created"), status: "success", duration: 3000 });
      setPartnerCode("");
      onSuccess?.();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Error";
      let message = detail;
      if (detail === "PARTNER_CODE_NOT_FOUND") message = t("partners.partnerLinks.codeNotFound", "No company found with this partner code");
      else if (detail === "CANNOT_LINK_TO_SELF") message = t("partners.partnerLinks.cannotLinkSelf", "Cannot link to your own company");
      else if (detail === "PARTNER_LINK_ALREADY_EXISTS") message = t("partners.partnerLinks.alreadyExists", "This partner link already exists");
      toast({ title: message, status: "error", duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>{t("partners.partnerLinks.addPartner", "Add Partner Company")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb={4} fontSize="sm" color="portal.muted" sx={{ marginBottom: "16px !important" }}>
            {t("partners.partnerLinks.addDescription", "Enter the partner code of the company you want to link with. This code is shared by the partner company.")}
          </Text>
          <FormControl isRequired>
            <FormLabel>{t("partners.partnerLinks.partnerCode", "Partner Code")}</FormLabel>
            <Input
              value={partnerCode}
              onChange={(e) => setPartnerCode(e.target.value)}
              placeholder={t("partners.partnerLinks.codePlaceholder", "e.g. a1b2-c3d4-e5f6-g7h8")}
              maxLength={19}
              pattern="[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}"
            />
          </FormControl>
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          <Button variant="secondary" size="sm" mr={3} onClick={onClose}>{t("common.cancel", "Cancel")}</Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} isLoading={loading}>
            {t("partners.partnerLinks.add", "Add Partner")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PartnerLinkFormModal;
